/**
 * Step 6: Confirm Upload
 * Shows summary of selected items and upload progress
 */

import {
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Progress,
  Tag,
  Badge,
  Spin,
  Button,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import type { DomainWithAnalysis, UploadResult } from '../types'
import { UPLOAD_BATCH_SIZE } from '../constants'
import { isPureDomain } from '../utils'

const { Text } = Typography

interface ConfirmStepProps {
  projectName?: string | null
  projectCrmId?: number | null
  domains: DomainWithAnalysis[]
  selectedPhones: Set<string>
  uploading: boolean
  uploadProgress: number
  uploadResults: UploadResult[]
  showAllDomains: boolean
  onShowAllDomainsChange: (show: boolean) => void
  onUpload: () => void
}

export default function ConfirmStep({
  projectName,
  projectCrmId,
  domains,
  selectedPhones,
  uploading,
  uploadProgress,
  uploadResults,
  showAllDomains,
  onShowAllDomainsChange,
  onUpload,
}: ConfirmStepProps) {
  const selectedDomains = domains.filter((d) => d.selected)

  // Separate into pure domains (VDL) and pages/URLs (local DB only)
  // Use source_type if available, otherwise check full_url
  const pureDomains = selectedDomains.filter(d =>
    d.source_type === 'domain' || (d.source_type === undefined && isPureDomain(d.full_url))
  )
  const pagesOnly = selectedDomains.filter(d =>
    d.source_type === 'url' || (d.source_type === undefined && !isPureDomain(d.full_url))
  )
  const phonesCount = selectedPhones.size

  const hasData = pureDomains.length > 0 || pagesOnly.length > 0 || phonesCount > 0

  if (!hasData) {
    return (
      <Alert message="Не выбрано ни одного источника" type="warning" showIcon />
    )
  }

  const displayLimit = 30

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <CloudUploadOutlined style={{ color: '#1890ff' }} />
            <span>Подготовка к загрузке</span>
          </Space>
        }
      >
        {uploading ? (
          <div style={{ padding: 40 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Spin size="large" />
              </div>

              <div>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>Загрузка источников в VDL</Text>
                  <Text type="secondary">{uploadProgress}%</Text>
                </div>
                <Progress
                  percent={uploadProgress}
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>

              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">
                  Загружено: {uploadResults.filter(r => r.success).length} / {selectedDomains.length} доменов
                </Text>
                {uploadResults.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Space size="small">
                      <Tag color="success">
                        Успешно: {uploadResults.filter(r => r.success && !r.is_duplicate).length}
                      </Tag>
                      <Tag color="warning">
                        Дубликаты: {uploadResults.filter(r => r.success && r.is_duplicate).length}
                      </Tag>
                      {uploadResults.filter(r => !r.success).length > 0 && (
                        <Tag color="error">
                          Ошибки: {uploadResults.filter(r => !r.success).length}
                        </Tag>
                      )}
                    </Space>
                  </div>
                )}
              </div>

              {/* List of successfully uploaded domains */}
              {uploadResults.filter(r => r.success).length > 0 && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Загруженные домены</span>
                      <Badge
                        count={uploadResults.filter(r => r.success).length}
                        style={{ backgroundColor: '#52c41a' }}
                      />
                    </Space>
                  }
                  style={{ maxHeight: 300, overflow: 'hidden' }}
                  styles={{ body: { padding: 12 } }}
                >
                  <div style={{
                    maxHeight: 250,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {uploadResults
                        .filter(r => r.success)
                        .map((result, index) => (
                          <div
                            key={`${result.domain}-${index}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 8px',
                              background: index % 2 === 0 ? '#fafafa' : '#fff',
                              borderRadius: 4,
                              fontSize: 13,
                            }}
                          >
                            <Space size="small">
                              <Text type="secondary" style={{ minWidth: 30, fontSize: 12 }}>
                                {index + 1}.
                              </Text>
                              <a
                                href={`https://${result.domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#1890ff',
                                  textDecoration: 'none',
                                  fontSize: 13,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {result.domain}
                              </a>
                            </Space>
                            <Space size="small">
                              {result.is_duplicate && (
                                <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>
                                  Дубликат
                                </Tag>
                              )}
                              {result.vdl_id && (
                                <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                                  ID: {result.vdl_id}
                                </Tag>
                              )}
                              {!result.is_duplicate && !result.vdl_id && (
                                <Tag color="success" style={{ margin: 0, fontSize: 11 }}>
                                  ✓
                                </Tag>
                              )}
                            </Space>
                          </div>
                        ))}
                    </Space>
                  </div>
                </Card>
              )}

              {/* List of failed domains */}
              {uploadResults.filter(r => !r.success).length > 0 && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      <span>Ошибки загрузки</span>
                      <Badge
                        count={uploadResults.filter(r => !r.success).length}
                        style={{ backgroundColor: '#ff4d4f' }}
                      />
                    </Space>
                  }
                  style={{ maxHeight: 200, overflow: 'hidden' }}
                  styles={{ body: { padding: 12 } }}
                >
                  <div style={{
                    maxHeight: 150,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {uploadResults
                        .filter(r => !r.success)
                        .map((result, index) => (
                          <div
                            key={`error-${result.domain}-${index}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 8px',
                              background: '#fff1f0',
                              borderRadius: 4,
                              fontSize: 13,
                              borderLeft: '3px solid #ff4d4f',
                            }}
                          >
                            <Text strong style={{ fontSize: 13 }}>
                              {result.domain}
                            </Text>
                            <Text type="danger" style={{ fontSize: 12 }}>
                              {result.error || 'Ошибка загрузки'}
                            </Text>
                          </div>
                        ))}
                    </Space>
                  </div>
                </Card>
              )}

              <Alert
                message="Загрузка выполняется батчами"
                description={`Домены загружаются порциями по ${UPLOAD_BATCH_SIZE} для избежания таймаутов. Пожалуйста, не закрывайте это окно.`}
                type="info"
                showIcon
              />
            </Space>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Project Info */}
            <Card size="small" type="inner">
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div><Text strong>Проект:</Text> <Text>{projectName}</Text></div>
                    <div><Text strong>CRM ID:</Text> <Tag color="blue">{projectCrmId}</Tag></div>
                  </Space>
                </Col>
                <Col>
                  <Space size="large">
                    {pureDomains.length > 0 && (
                      <Statistic
                        title={<Text type="secondary" style={{ fontSize: 12 }}>Домены → VDL</Text>}
                        value={pureDomains.length}
                        valueStyle={{ color: '#1890ff', fontSize: 20 }}
                      />
                    )}
                    {pagesOnly.length > 0 && (
                      <Statistic
                        title={<Text type="secondary" style={{ fontSize: 12 }}>Страницы → БД</Text>}
                        value={pagesOnly.length}
                        valueStyle={{ color: '#722ed1', fontSize: 20 }}
                      />
                    )}
                    {phonesCount > 0 && (
                      <Statistic
                        title={<Text type="secondary" style={{ fontSize: 12 }}>Телефоны → VDL</Text>}
                        value={phonesCount}
                        valueStyle={{ color: '#52c41a', fontSize: 20 }}
                      />
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* VDL Upload Section - Domains */}
            {pureDomains.length > 0 && (
              <Card
                size="small"
                title={
                  <Space>
                    <CloudUploadOutlined style={{ color: '#1890ff' }} />
                    <span>Домены для загрузки в VDL</span>
                    <Badge count={pureDomains.length} style={{ backgroundColor: '#1890ff' }} />
                  </Space>
                }
                style={{ maxHeight: 300, overflow: 'auto' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {pureDomains.slice(0, showAllDomains ? undefined : displayLimit).map((domain, index) => (
                    <div
                      key={domain.host}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        background: index % 2 === 0 ? '#f0f5ff' : '#fff',
                        borderRadius: 4,
                        borderLeft: '3px solid #1890ff',
                      }}
                    >
                      <Space>
                        <Text type="secondary" style={{ minWidth: 30, fontSize: 12 }}>{index + 1}.</Text>
                        <Text strong style={{ fontSize: 13 }}>{domain.host}</Text>
                      </Space>
                      <Space size="small">
                        {domain.ai_score !== undefined && domain.ai_score !== null && (
                          <Badge
                            count={domain.ai_score.toFixed(2)}
                            style={{
                              backgroundColor: domain.ai_score > 0.7 ? '#52c41a' : domain.ai_score > 0.4 ? '#faad14' : '#ff4d4f',
                              fontSize: 10,
                            }}
                          />
                        )}
                        {domain.ai_recommendation && (
                          <Tag
                            color={domain.ai_recommendation === 'approved' ? 'success' : domain.ai_recommendation === 'rejected' ? 'error' : 'warning'}
                            style={{ margin: 0, fontSize: 10 }}
                          >
                            {domain.ai_recommendation === 'approved' ? 'AI ✓' : domain.ai_recommendation === 'rejected' ? 'AI ✗' : 'AI ?'}
                          </Tag>
                        )}
                      </Space>
                    </div>
                  ))}
                  {!showAllDomains && pureDomains.length > displayLimit && (
                    <Button type="link" size="small" onClick={() => onShowAllDomainsChange(true)}>
                      Показать все ({pureDomains.length})
                    </Button>
                  )}
                </Space>
              </Card>
            )}

            {/* Local DB Section - Pages */}
            {pagesOnly.length > 0 && (
              <Card
                size="small"
                title={
                  <Space>
                    <DatabaseOutlined style={{ color: '#722ed1' }} />
                    <span>Страницы для сохранения в БД</span>
                    <Badge count={pagesOnly.length} style={{ backgroundColor: '#722ed1' }} />
                  </Space>
                }
                style={{ maxHeight: 200, overflow: 'auto' }}
              >
                <Alert
                  message="Страницы с путями сохраняются только в локальную базу данных"
                  type="info"
                  showIcon
                  style={{ marginBottom: 12, fontSize: 12 }}
                />
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {pagesOnly.slice(0, 10).map((page, index) => (
                    <div
                      key={page.full_url || page.host}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        background: index % 2 === 0 ? '#f9f0ff' : '#fff',
                        borderRadius: 4,
                        borderLeft: '3px solid #722ed1',
                      }}
                    >
                      <Space>
                        <Text type="secondary" style={{ minWidth: 30, fontSize: 12 }}>{index + 1}.</Text>
                        <a
                          href={page.full_url || `https://${page.host}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#722ed1',
                            textDecoration: 'none',
                            fontSize: 13,
                            wordBreak: 'break-all',
                          }}
                        >
                          {page.full_url || page.host}
                        </a>
                      </Space>
                      <Space size="small">
                        {page.ai_score !== undefined && page.ai_score !== null && (
                          <Badge
                            count={page.ai_score.toFixed(2)}
                            style={{
                              backgroundColor: page.ai_score > 0.7 ? '#52c41a' : page.ai_score > 0.4 ? '#faad14' : '#ff4d4f',
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Space>
                    </div>
                  ))}
                  {pagesOnly.length > 10 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>... и ещё {pagesOnly.length - 10} страниц</Text>
                  )}
                </Space>
              </Card>
            )}

            {/* VDL Upload Section - Phones */}
            {phonesCount > 0 && (
              <Card
                size="small"
                title={
                  <Space>
                    <PhoneOutlined style={{ color: '#52c41a' }} />
                    <span>Телефоны для загрузки в VDL</span>
                    <Badge count={phonesCount} style={{ backgroundColor: '#52c41a' }} />
                  </Space>
                }
                style={{ maxHeight: 200, overflow: 'auto' }}
              >
                <Space wrap size="small">
                  {Array.from(selectedPhones).slice(0, 20).map((phone) => (
                    <Tag key={phone} color="green" style={{ margin: 2 }}>
                      {phone}
                    </Tag>
                  ))}
                  {phonesCount > 20 && (
                    <Tag color="default">... и ещё {phonesCount - 20}</Tag>
                  )}
                </Space>
              </Card>
            )}

            {/* Info Alert */}
            <Alert
              message="Подготовка к загрузке"
              description={
                <Space direction="vertical" size={4}>
                  {pureDomains.length > 0 && (
                    <Text><CloudUploadOutlined style={{ color: '#1890ff', marginRight: 8 }} />Домены ({pureDomains.length}) будут загружены в VDL со статусом «Не работает»</Text>
                  )}
                  {pagesOnly.length > 0 && (
                    <Text><DatabaseOutlined style={{ color: '#722ed1', marginRight: 8 }} />Страницы ({pagesOnly.length}) будут сохранены в локальную базу данных</Text>
                  )}
                  {phonesCount > 0 && (
                    <Text><PhoneOutlined style={{ color: '#52c41a', marginRight: 8 }} />Телефоны ({phonesCount}) будут загружены в VDL</Text>
                  )}
                </Space>
              }
              type="info"
              showIcon
            />

            {/* Action Button */}
            <Button
              type="primary"
              size="large"
              icon={<CloudUploadOutlined />}
              onClick={onUpload}
              block
              disabled={pureDomains.length === 0 && pagesOnly.length === 0 && phonesCount === 0}
            >
              {pureDomains.length > 0 && phonesCount > 0
                ? `Загрузить в VDL (${pureDomains.length} доменов, ${phonesCount} телефонов)`
                : pureDomains.length > 0
                  ? `Загрузить в VDL (${pureDomains.length} доменов)`
                  : phonesCount > 0
                    ? `Загрузить в VDL (${phonesCount} телефонов)`
                    : pagesOnly.length > 0
                      ? `Сохранить в базу данных (${pagesOnly.length} страниц)`
                      : 'Сохранить в базу данных'}
            </Button>
          </Space>
        )}
      </Card>
    </Space>
  )
}
